import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLock } from '../context/LockContext';
import { useAuth } from '../context/AuthContext';
import './PinPad.css';
import './LockScreen.css';

function LockScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPin, setPin, unlock, lockoutUntil, mustLogout } = useLock();
  const { logout } = useAuth();
  const [pin, setLocalPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  const [loggingOut, setLoggingOut] = useState(false);

  // Forzar logout cuando se alcanzan 10 intentos
  useEffect(() => {
    if (mustLogout) {
      const doLogout = async () => {
        setLoggingOut(true);
        try {
          await logout();
          navigate('/login');
        } catch (err) {
          console.error('Error forced logout:', err);
        }
      };
      doLogout();
    }
  }, [mustLogout, logout, navigate]);

  // Countdown timer para lockout
  useEffect(() => {
    if (lockoutUntil) {
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
        setCountdown(remaining);
        if (remaining <= 0) {
          setError('');
          if (countdownRef.current) clearInterval(countdownRef.current);
        }
      };
      tick();
      countdownRef.current = setInterval(tick, 1000);
      return () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    } else {
      setCountdown(0);
    }
  }, [lockoutUntil]);

  const isLockedOut = lockoutUntil && Date.now() < lockoutUntil;

  const isSetupMode = !hasPin;

  const handleLogout = async () => {
    const confirmed = window.confirm(t('lock.logoutConfirm', '¿Cerrar sesión? Se redirigirá al login.'));
    if (!confirmed) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
    } finally {
      setLoggingOut(false);
    }
  };

  const handleNumberClick = useCallback((num) => {
    if (error) setError('');
    if (shake) setShake(false);

    if (isSetupMode) {
      if (isConfirming) {
        if (confirmPin.length < 4) {
          const newConfirmPin = confirmPin + num;
          setConfirmPin(newConfirmPin);

          if (newConfirmPin.length === 4) {
            // Validar que coincidan
            if (newConfirmPin === pin) {
              setPin(newConfirmPin).then(() => {
                setError('');
              }).catch(() => {
                setError(t('lock.setupError'));
                setTimeout(() => {
                  setLocalPin('');
                  setConfirmPin('');
                  setIsConfirming(false);
                }, 1500);
              });
            } else {
              setError(t('lock.pinMismatch'));
              setShake(true);
              setTimeout(() => {
                setLocalPin('');
                setConfirmPin('');
                setIsConfirming(false);
                setShake(false);
              }, 1500);
            }
          }
        }
      } else {
        if (pin.length < 4) {
          const newPin = pin + num;
          setLocalPin(newPin);

          if (newPin.length === 4) {
            // Pedir confirmación
            setTimeout(() => {
              setIsConfirming(true);
            }, 200);
          }
        }
      }
    } else {
      // Modo desbloqueo
      if (isLockedOut) return;
      if (pin.length < 4) {
        const newPin = pin + num;
        setLocalPin(newPin);

        if (newPin.length === 4) {
          unlock(newPin).then((result) => {
            if (!result.success) {
              if (result.locked) {
                setError(t('lock.lockedOut'));
              } else {
                // Mostrar intentos restantes hasta próximo lockout
                const nextThreshold = result.attempts < 3 ? 3 : result.attempts < 6 ? 6 : 10;
                const remaining = nextThreshold - result.attempts;
                setError(t('lock.incorrectPinAttempts', {
                  remaining,
                  count: remaining,
                }));
              }
              setShake(true);
              setTimeout(() => {
                setLocalPin('');
                setShake(false);
              }, 1000);
            }
          });
        }
      }
    }
  }, [error, shake, isSetupMode, isConfirming, confirmPin, pin, setPin, unlock, t, isLockedOut]);

  const handleBackspace = useCallback(() => {
    if (error) setError('');
    if (shake) setShake(false);

    if (isSetupMode) {
      if (isConfirming) {
        setConfirmPin(prev => prev.slice(0, -1));
      } else {
        setLocalPin(prev => prev.slice(0, -1));
      }
    } else {
      setLocalPin(prev => prev.slice(0, -1));
    }
  }, [error, shake, isSetupMode, isConfirming]);

  // Soporte para teclado físico
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Números del teclado numérico o teclado normal
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        handleNumberClick(event.key);
      }
      // Backspace
      else if (event.key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
      }
      // Enter - no hace nada específico pero previene submit de forms
      else if (event.key === 'Enter') {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBackspace, handleNumberClick]);

  const currentPin = isSetupMode && isConfirming ? confirmPin : pin;

  return (
    <div className="lock-screen-overlay">
      <div className="lock-screen-container">
        <div className="lock-icon">
          🔒
        </div>

        <h2 className="lock-title">
          {isSetupMode
            ? (isConfirming ? t('lock.confirmPin') : t('lock.setupPin'))
            : t('lock.enterPin')}
        </h2>

        {isLockedOut && (
          <div className="lock-error" role="alert">
            {t('lock.lockoutCountdown', { seconds: countdown })}
          </div>
        )}

        {error && !isLockedOut && (
          <div className="lock-error" role="alert">
            {error}
          </div>
        )}

        <div className={`pin-dots ${shake ? 'shake' : ''}`}>
          {[0, 1, 2, 3].map(index => (
            <div
              key={index}
              className={`pin-dot ${currentPin.length > index ? 'filled' : ''}`}
            />
          ))}
        </div>

        <div className="pin-keypad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button
              key={num}
              className="pin-button"
              onClick={() => handleNumberClick(num.toString())}
              disabled={isLockedOut}
            >
              {num}
            </button>
          ))}
          <div className="pin-button empty" aria-hidden="true" />
          <button
            className="pin-button"
            onClick={() => handleNumberClick('0')}
            disabled={isLockedOut}
          >
            0
          </button>
          <button
            className="pin-button backspace"
            onClick={handleBackspace}
            disabled={isLockedOut}
            aria-label={t('lock.backspace', 'Backspace')}
          >
            <span className="material-icons" aria-hidden="true">backspace</span>
          </button>
        </div>

        {isSetupMode && (
          <p className="lock-hint">
            {t('lock.setupHint')}
          </p>
        )}

        {!isSetupMode && (
          <button
            className="lock-logout-btn"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? t('common.loading', 'Cargando...') : t('lock.logout', 'Cerrar sesión')}
          </button>
        )}
      </div>
    </div>
  );
}

export default LockScreen;
